export interface ReportArgument {
  key: string;
  label: string;
  value: string | boolean | null;
  display_value: string | boolean;
}

export interface FormSchemaField {
  formtype: string;
  sortlevel: number;
  options?: string;
  multiple?: boolean;
  fieldtype: string;
  title: string;
  key: string;
  mandatory?: boolean;
  default?: boolean;
  help?: string;
}

export interface GridColumnState {
  colId: string;
  width: number;
  hide: boolean;
  pinned: string | null;
  sort: string | null;
  sortIndex: number | null;
  aggFunc: string | null;
  rowGroup: boolean;
  rowGroupIndex: number | null;
  pivot: boolean;
  pivotIndex: number | null;
  flex: number | null;
}

export interface GridSettings {
  columnState: GridColumnState[];
  filterSettings: Record<string, any>;
  pivotMode: boolean;
  chartSettings: any[];
}

export interface ReportActions {
  share: boolean;
  delete: boolean;
  edit: boolean;
  rename: boolean;
  favorite: boolean;
  dashboard: boolean;
}

export interface TemplateReportModel {
  title: string;
  report_name: string;
  schema_argument: ReportArgument[];
  formSchema: FormSchemaField[];
  grid_settings: string; // raw JSON string (can be parsed to GridSettings if needed)
  report_rid: string;
  rid: string;
  report_category: string;
  actions: ReportActions;
  lines: any[];
  type: ReportType;
}

export enum ReportType {
  template = 'Template',
  report = 'Report'
}

export interface ReportInfo {
  title: string;
  actions: Actions;
  stage: string;
  cardType: string;
  card_type_label: string;
  report_category: string;
  description: string;
  image: string;
  'image-default': string;
  target: null;
  targetType: string;
  color: string;
  rid: string;
  name: string;
  inputs: Input[];
  options: string;
  isFrom: string;
  type: ReportType;
  reportRid: string;
  parameters: any[];
}

export type ReportTypes = ReportInfo | TemplateReportModel;

export interface Actions {
  delete: boolean;
}

export interface Input {
  formtype: string;
  options?: string;
  multiple?: boolean;
  sortlevel: number;
  fieldtype: string;
  title: string;
  key: string;
  mandatory?: boolean;
  default?: boolean;
  help?: string;
}

export interface ReportJobResponse {
  job_rid: string;
  message: string;
  keep_polling: boolean;
}

export interface BaseReport {
  name: string;
  title?: string;
  lines: Array<Line[]>;
  isGrid: boolean;
  pivot_settings: PivotSettings;
  formSchema: FormSchema[]; // Standardize the type here
  powerBIUrl?: string;
  actions?: ReportActions;
}

// 2. Specific for the background configuration
export interface BackgroundReport extends BaseReport {
  title: string; // Override to make it required
  report_rid: string;
  card_type_label: string;
  image: string;
  'image-default': string;
  parameters: Parameter[];
  grid_settings: any | null;
  is_grid: any | null;
  chart_settings: any | null;
  is_chart: any | null;
}

// 3. Specific for the processed/polling data
export interface ReportsFlattenedData extends BaseReport {
  backgroundReportRid: string;
  keep_polling: boolean;
  link: string;
  timed_out: boolean;
  timeout_message: string | null;
  error: string | null;
  content_type: string | null;
  message?: string;
}

export interface Line {
  value?: boolean | string | number;
  key: string;
  sortlevel: number;
  label: string;
  hidden?: boolean;
  display_value: number | string;
  rid?: string;
  link?: Link;
  order?: number;
  is_compliant?: boolean;
  is_inferred?: boolean;
  color?: string;
  bgcolor?: string;
}

export interface Link {
  url: string;
  type: string;
  method: string;
}

export interface PivotSettings {
  row_groups: any[];
  values: any[];
  column_labels: any[];
  pivot_mode: boolean;
}

export interface SidePanelData {
  actions?: ReportActions; // depends on backend
  isFrom: 'pivot-manager'; // literal type (safer)
  type?: string;
  name: string;
  reportRid: string;
  inputs?: FormSchemaField[];
  parameters?: any[];
  background: boolean;
  powerBIUrl?: string;
  backgroundReportRid?: string;
  category?: string;
  display_value?: string;
  rid?: string;
  unit?: string;
  label?: string;
  cardType?: string;
}

export interface Actions {
  share: boolean;
  delete: boolean;
  freeze: boolean;
  approve: boolean;
  edit: boolean;
  recalculate: boolean;
}

export interface FormSchema {
  formtype: string;
  options?: string;
  multiple?: boolean;
  sortlevel: number;
  fieldtype: string;
  title: string;
  key: string;
  mandatory: boolean;
  default?: boolean;
  help?: string;
}

export interface Parameter {
  key: string;
  label: string;
  value: string;
  display_value: string;
  rid: string;
}

export interface ReportPollingResponse {
  keep_polling: boolean;
  report_rid: string;
  link: string;
  timed_out: boolean;
  timeout_message: null;
  error: null;
  streaming_url: string;
  content_type: null;
  skip_file_size: boolean;
  is_csv: boolean;
}
